import React, { useState } from "react";
import moment from "moment";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import PageContainer from "../../../../../components/PageContainer";
import DataTable from "../../../../../components/DataTable";
import StatusBadge from "../../../../../components/StatusBadge";
import EmptyValue from "../../../../../components/EmptyValue";
import usePurchaseOrderDetail from "../../../../hooks/usePurchaseOrderDetail";
import useReceivePurchaseOrderLines from "../../../../hooks/useReceivePurchaseOrderLines";
import useCancelPurchaseOrder from "../../../../hooks/useCancelPurchaseOrder";

// Purchase order detail — GET/PATCH /api/inventory/orders/:id[/receive|/cancel]. Moved
// from src/pages/inventory-procurement/purchase-orders/detail/index.jsx into the
// pharmacy v2 console, rebuilt on PageContainer/DataTable/StatusBadge instead of the
// page-local Table/StatusBadge/CtaButton set (now deleted).
//
// See ../index.jsx for why `inventoryOrders.status` gets its own small tone map instead
// of routing through src/v2/constants/order-status.js's STATUS_TONE_CLASSES registry
// (that registry is for `userOrders.status` specifically).
const STATUS_TONE = {
    ORDERED: "info",
    PARTIALLY_RECEIVED: "warning",
    RECEIVED: "success",
    CANCELLED: "error",
};

const RECEIVABLE_LINE_STATUSES = ["ORDERED", "PARTIALLY_RECEIVED"];

const CARD = "bg-[var(--ops-carbon-25)] border border-[var(--ops-carbon-200)] rounded-2xl shadow-[var(--ops-shadow)] p-5";

function PurchaseOrderDetailPage() {
    const { id } = useParams();
    const { data: order, isLoading } = usePurchaseOrderDetail(id);
    const receiveLines = useReceivePurchaseOrderLines();
    const cancelOrder = useCancelPurchaseOrder(id);

    // Per-line entered-quantity draft, keyed by inventoryOrderItem id. `quantityReceived`
    // on the backend is a DELTA added to the line's running total, not a new absolute
    // total — this input is that same delta: "how many arrived just now."
    const [receiveDrafts, setReceiveDrafts] = useState({});
    const [savingLineId, setSavingLineId] = useState(null);
    const [showCancelForm, setShowCancelForm] = useState(false);
    const [cancelReason, setCancelReason] = useState("");

    const lines = order?.inventoryOrderItems || [];

    const setDraft = (lineId, value) => {
        setReceiveDrafts((prev) => ({ ...prev, [lineId]: value }));
    };

    const fillRemaining = (line) => {
        setDraft(line.id, String(line.quantityOrdered - line.quantityReceived));
    };

    const submitReceive = async (linesToSubmit) => {
        try {
            await receiveLines.mutateAsync({
                orderId: id,
                lines: linesToSubmit.map((line) => ({
                    inventoryOrderItemId: line.id,
                    quantityReceived: parseInt(receiveDrafts[line.id], 10),
                })),
            });
            toast.success("Receipt recorded");
            setReceiveDrafts((prev) => {
                const next = { ...prev };
                linesToSubmit.forEach((line) => delete next[line.id]);
                return next;
            });
            return true;
        } catch (error) {
            toast.error(error?.err || "Error recording receipt");
            return false;
        }
    };

    const receiveLine = async (line) => {
        const quantity = parseInt(receiveDrafts[line.id], 10);
        if (!quantity || quantity <= 0) {
            toast.warn("Enter a quantity received first");
            return;
        }
        setSavingLineId(line.id);
        await submitReceive([line]);
        setSavingLineId(null);
    };

    const receiveAllEntered = async () => {
        const linesToSubmit = lines.filter((line) => parseInt(receiveDrafts[line.id], 10) > 0);
        if (linesToSubmit.length === 0) {
            toast.warn("Enter a quantity received for at least one line");
            return;
        }
        await submitReceive(linesToSubmit);
    };

    const submitCancel = async () => {
        if (!cancelReason.trim()) {
            toast.warn("A cancellation reason is required");
            return;
        }
        try {
            await cancelOrder.mutateAsync(cancelReason.trim());
            toast.success("Purchase order cancelled");
            setShowCancelForm(false);
            setCancelReason("");
        } catch (error) {
            toast.error(error?.err || "Error cancelling purchase order");
        }
    };

    if (isLoading) {
        return (
            <PageContainer>
                <p className="text-[var(--ops-carbon-500)]">Loading purchase order…</p>
            </PageContainer>
        );
    }

    if (!order) {
        return (
            <PageContainer>
                <div className="flex flex-col gap-2">
                    <p className="text-[var(--ops-carbon-500)]">Purchase order not found.</p>
                    <Link to="/v2/inventory/procurement/purchase-orders" className="text-sm font-semibold text-[var(--ops-emerald-600)] hover:underline">
                        ← Back to purchase orders
                    </Link>
                </div>
            </PageContainer>
        );
    }

    const canReceive = RECEIVABLE_LINE_STATUSES.includes(order.status);
    const canCancel = order.status !== "RECEIVED" && order.status !== "CANCELLED";

    const lineColumns = [
        { key: "medicine", label: "Medicine", render: (line) => <span className="font-semibold">{line.item?.name || <EmptyValue />}</span> },
        { key: "quantityOrdered", label: "Ordered" },
        { key: "quantityReceived", label: "Received" },
        {
            key: "remaining",
            label: "Remaining",
            render: (line) => {
                const remaining = line.quantityOrdered - line.quantityReceived;
                return <strong className={remaining > 0 ? "text-[var(--ops-amber-700)]" : "text-[var(--ops-carbon-400)]"}>{remaining}</strong>;
            },
        },
        { key: "status", label: "Status", render: (line) => <StatusBadge label={line.status} tone={STATUS_TONE[line.status] || "neutral"} /> },
        {
            key: "receiveQuantity",
            label: "Receive quantity",
            render: (line) => {
                const remaining = line.quantityOrdered - line.quantityReceived;
                const lineReceivable = RECEIVABLE_LINE_STATUSES.includes(line.status) && remaining > 0;
                if (!lineReceivable) return <EmptyValue />;
                return (
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            min="1"
                            max={remaining}
                            className="w-20 h-9 border border-[var(--ops-carbon-200)] rounded-lg px-2 text-sm"
                            value={receiveDrafts[line.id] || ""}
                            onChange={(event) => setDraft(line.id, event.target.value)}
                            placeholder="0"
                        />
                        <button type="button" className="text-xs font-semibold text-[var(--ops-emerald-600)] hover:underline" onClick={() => fillRemaining(line)}>
                            Fill remaining
                        </button>
                    </div>
                );
            },
        },
        {
            key: "action",
            label: "Action",
            render: (line) => {
                const remaining = line.quantityOrdered - line.quantityReceived;
                const lineReceivable = RECEIVABLE_LINE_STATUSES.includes(line.status) && remaining > 0;
                if (!lineReceivable) return null;
                return (
                    <button
                        type="button"
                        className="px-3 py-1.5 rounded-lg bg-[var(--ops-emerald-100)] text-[var(--ops-emerald-600)] border border-[var(--ops-emerald-200)] text-xs font-semibold disabled:opacity-50"
                        onClick={() => receiveLine(line)}
                        disabled={savingLineId === line.id}
                    >
                        {savingLineId === line.id ? "Saving…" : "Receive"}
                    </button>
                );
            },
        },
    ];

    const reservationRows = lines.flatMap((line) =>
        (line.reservations || []).map((reservation) => ({ ...reservation, medicineName: line.item?.name }))
    );

    const reservationColumns = [
        { key: "medicine", label: "Medicine", render: (reservation) => reservation.medicineName || <EmptyValue /> },
        { key: "userOrderId", label: "User order", render: (reservation) => `#${reservation.userOrderId}` },
        { key: "quantityReserved", label: "Quantity reserved" },
        {
            key: "status",
            label: "Status",
            render: (reservation) =>
                reservation.active ? (
                    <span className="text-xs font-semibold text-[var(--ops-evergreen-600)]">Active</span>
                ) : (
                    <span className="text-xs text-[var(--ops-carbon-400)]">
                        Released{!reservation.releasedAt && " (order no longer active)"}
                    </span>
                ),
        },
    ];

    return (
        <PageContainer>
            <Link to="/v2/inventory/procurement/purchase-orders" className="text-sm font-semibold text-[var(--ops-emerald-600)] hover:underline">
                ← Back to purchase orders
            </Link>

            <div className={`${CARD} mt-4`}>
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <h1 className="m-0 text-lg font-bold text-[var(--ops-carbon-900)]">PO #{order.id}</h1>
                        <p className="mt-1 text-xs text-[var(--ops-carbon-500)]">
                            {order.vendor?.name || "—"} {order.vendor?.type ? `(${order.vendor.type})` : ""}
                        </p>
                    </div>
                    <StatusBadge label={order.status} tone={STATUS_TONE[order.status] || "neutral"} />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 text-sm">
                    <div>
                        <p className="text-xs text-[var(--ops-carbon-500)]">Placed at</p>
                        <div className="text-[var(--ops-carbon-900)]">{order.placedAt ? moment(order.placedAt).format("DD MMM YYYY, h:mm A") : <EmptyValue />}</div>
                    </div>
                    <div>
                        <p className="text-xs text-[var(--ops-carbon-500)]">Placed by</p>
                        {/* `placedByName` is resolved server-side from the MyTatva userId
                            stored in `placedBy` (classes/InventoryOrder/Functions.js).
                            Falls back to the raw id rather than to nothing: an unresolvable
                            user is still better identified by its id than by a dash. */}
                        <div className="text-[var(--ops-carbon-900)]">
                            {order.placedByName || order.placedBy || <EmptyValue />}
                        </div>
                        {order.placedByName && order.placedBy && (
                            <div className="text-xs text-[var(--ops-carbon-500)] break-all">{order.placedBy}</div>
                        )}
                    </div>
                    <div>
                        <p className="text-xs text-[var(--ops-carbon-500)]">Received at</p>
                        <div className="text-[var(--ops-carbon-900)]">{order.receivedAt ? moment(order.receivedAt).format("DD MMM YYYY, h:mm A") : <EmptyValue />}</div>
                    </div>
                    <div>
                        <p className="text-xs text-[var(--ops-carbon-500)]">Notes</p>
                        <div className="text-[var(--ops-carbon-900)]">{order.notes || <EmptyValue />}</div>
                    </div>
                </div>

                {order.status === "CANCELLED" && order.cancelReason && (
                    <p className="mt-3 text-sm text-[var(--ops-crimson-600)]">Cancelled: {order.cancelReason}</p>
                )}
            </div>

            <div className={`${CARD} mt-4`}>
                <div className="flex items-center justify-between mb-2">
                    <h2 className="m-0 font-semibold text-[var(--ops-carbon-900)]">Lines</h2>
                    {canReceive && (
                        <button
                            type="button"
                            onClick={receiveAllEntered}
                            disabled={receiveLines.isLoading}
                            className="bg-elm-500 text-white-50 px-4 py-2 rounded-lg font-sans font-semibold disabled:opacity-50"
                        >
                            {receiveLines.isLoading ? "Saving…" : "Receive all entered"}
                        </button>
                    )}
                </div>
                <DataTable columns={lineColumns} rows={lines} />
            </div>

            <div className={`${CARD} mt-4`}>
                <h2 className="m-0 mb-3 font-semibold text-[var(--ops-carbon-900)]">Reservations — patient orders this stock is earmarked for</h2>
                {reservationRows.length === 0 ? (
                    <p className="text-xs text-[var(--ops-carbon-500)]">No reservations on this purchase order.</p>
                ) : (
                    <DataTable columns={reservationColumns} rows={reservationRows} />
                )}
            </div>

            {canCancel && (
                <div className={`${CARD} mt-4`}>
                    {!showCancelForm ? (
                        <button
                            type="button"
                            className="px-4 py-2 rounded-lg border border-[var(--ops-crimson-200)] bg-[var(--ops-crimson-50)] text-[var(--ops-crimson-600)] text-sm font-semibold"
                            onClick={() => setShowCancelForm(true)}
                        >
                            Cancel purchase order
                        </button>
                    ) : (
                        <div className="flex items-start gap-2 flex-wrap">
                            <textarea
                                className="flex-1 min-w-[240px] border border-[var(--ops-carbon-200)] rounded-lg p-2 text-sm"
                                rows={2}
                                placeholder="Reason for cancelling (required)"
                                value={cancelReason}
                                onChange={(event) => setCancelReason(event.target.value)}
                            />
                            <button
                                type="button"
                                className="px-4 py-2 rounded-lg bg-[var(--ops-crimson-600)] text-[var(--ops-carbon-25)] text-sm font-semibold disabled:opacity-50"
                                onClick={submitCancel}
                                disabled={cancelOrder.isLoading}
                            >
                                {cancelOrder.isLoading ? "Cancelling…" : "Confirm cancel"}
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2 rounded-lg border border-[var(--ops-carbon-200)] text-sm font-semibold"
                                onClick={() => {
                                    setShowCancelForm(false);
                                    setCancelReason("");
                                }}
                            >
                                Back
                            </button>
                        </div>
                    )}
                </div>
            )}
        </PageContainer>
    );
}

export default PurchaseOrderDetailPage;
